import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";
import { Bell, Check, CheckCheck, Sparkles, Heart, Plus, MessageCircle, Camera, SmilePlus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const NotificationIcon = ({ type }) => {
  switch (type) {
    case "item_completed":
      return <Sparkles className="w-5 h-5 text-secondary" />;
    case "item_added":
      return <Plus className="w-5 h-5 text-primary" />;
    case "partner_linked":
      return <Heart className="w-5 h-5 text-coral" />;
    case "comment_added":
      return <MessageCircle className="w-5 h-5 text-blue-500" />;
    case "photo_added":
      return <Camera className="w-5 h-5 text-purple-500" />;
    case "reaction_added":
      return <SmilePlus className="w-5 h-5 text-amber-500" />;
    default:
      return <Bell className="w-5 h-5 text-muted-foreground" />;
  }
};

export const NotificationBell = ({ onNotificationClick }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.count);
    } catch (error) {
      console.error("Failed to fetch unread count");
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data);
    } catch (error) {
      console.error("Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleOpen = () => {
    setShowPanel(true);
    fetchNotifications();
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark as read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read");
    }
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${notificationId}`);
      setNotifications(notifications.filter(n => n.id !== notificationId));
      // Update unread count if deleted notification was unread
      const deleted = notifications.find(n => n.id === notificationId);
      if (deleted && !deleted.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Failed to delete notification");
      toast.error("Failed to delete notification");
    }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${API}/notifications`);
      setNotifications([]);
      setUnreadCount(0);
      toast.success("All notifications cleared");
    } catch (error) {
      console.error("Failed to clear all notifications");
      toast.error("Failed to clear notifications");
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleOpen}
        className="relative rounded-full"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-charcoal" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      <Dialog open={showPanel} onOpenChange={setShowPanel}>
        <DialogContent className="max-w-md bg-card border-none rounded-2xl p-0 max-h-[80vh] overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b border-charcoal/5">
            <div className="flex items-center justify-between">
              <DialogTitle className="font-serif text-xl text-charcoal">
                Notifications
              </DialogTitle>
              <div className="flex items-center gap-2">
                {notifications.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearAll}
                    className="text-sm text-coral hover:text-coral/80"
                    data-testid="clear-all-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Clear all
                  </Button>
                )}
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMarkAllRead}
                    className="text-sm text-primary hover:text-primary/80"
                    data-testid="mark-all-read-btn"
                  >
                    <CheckCheck className="w-4 h-4 mr-1" />
                    Mark all read
                  </Button>
                )}
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh]">
            {loading ? (
              <div className="p-6 text-center text-muted-foreground">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-charcoal/5">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-muted/50 transition-colors cursor-pointer ${
                      !notification.read ? "bg-primary/5" : ""
                    }`}
                    data-testid={`notification-${notification.id}`}
                    onClick={() => {
                      // Mark as read
                      if (!notification.read) {
                        handleMarkRead(notification.id);
                      }
                      // If notification has item_id, trigger callback to open item
                      if (notification.item_id && onNotificationClick) {
                        setShowPanel(false);
                        onNotificationClick(notification.item_id);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0">
                        <NotificationIcon type={notification.type} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-charcoal text-sm">
                          {notification.title}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </p>
                          {notification.item_id && (
                            <span className="text-xs text-primary font-medium">
                              View item →
                            </span>
                          )}
                        </div>
                      </div>

                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(notification.id);
                          }}
                          className="h-8 w-8 flex-shrink-0"
                          data-testid={`mark-read-${notification.id}`}
                        >
                          <Check className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleDeleteNotification(notification.id, e)}
                        className="h-8 w-8 flex-shrink-0 hover:bg-coral/10"
                        data-testid={`delete-notification-${notification.id}`}
                      >
                        <X className="w-4 h-4 text-muted-foreground hover:text-coral" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBell;
